import React, { useState } from 'react';
import { Stack, Chip, Tooltip, Dialog, DialogTitle, DialogContent, Typography, Box, IconButton, Link } from '@mui/material';
import { Warning, ReportProblem, Info, Close, ExpandMore, OpenInNew } from '@mui/icons-material';

interface FraudIndicator {
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
  score_impact: number;
  evidence?: any;
  details?: any; // Backward compatibility
}

interface FraudIndicatorChipsProps {
  indicators: FraudIndicator[];
  maxDisplay?: number;
  showDetails?: boolean;
}

const FraudIndicatorChips: React.FC<FraudIndicatorChipsProps> = ({ 
  indicators, 
  maxDisplay = 3,
  showDetails = true
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Debug log to see what data we're receiving
  console.log('FraudIndicatorChips received indicators:', indicators);

  if (!indicators || indicators.length === 0) {
    return null;
  }

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'default' => {
    switch(severity.toUpperCase()) {
      case 'CRITICAL': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch(severity.toUpperCase()) {
      case 'CRITICAL': return <ReportProblem />;
      case 'HIGH': return <Warning />;
      case 'MEDIUM': return <Info />;
      default: return <Info />;
    }
  };

  const displayIndicators = indicators.slice(0, maxDisplay);
  const remainingCount = indicators.length - maxDisplay;

  const handleDetailClick = () => {
    if (showDetails) {
      setDialogOpen(true);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
        {displayIndicators.map((indicator, index) => (
          <Tooltip
            key={index}
            title={`${indicator.severity}: ${indicator.description}${indicator.score_impact ? ` (Impact: ${indicator.score_impact > 0 ? '+' : ''}${indicator.score_impact})` : ''}`}
            arrow
          >
            <Chip
              label={indicator.type.replace(/_/g, ' ')}
              size="small"
              color={getSeverityColor(indicator.severity)}
              icon={getSeverityIcon(indicator.severity)}
              sx={{ 
                mb: 0.5,
                cursor: showDetails ? 'pointer' : 'default',
                '& .MuiChip-icon': { fontSize: 14 }
              }}
              onClick={showDetails ? handleDetailClick : undefined}
            />
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Chip
            label={`+${remainingCount} more`}
            size="small"
            variant="outlined"
            icon={<ExpandMore />}
            sx={{ 
              mb: 0.5,
              cursor: showDetails ? 'pointer' : 'default'
            }}
            onClick={showDetails ? handleDetailClick : undefined}
          />
        )}
      </Stack>

      {/* Enhanced Details Dialog */}
      {showDetails && (
        <Dialog 
          open={dialogOpen} 
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            pb: 2
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Fraud Risk Indicators
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="large">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 3, py: 3 }}>
            {/* Group indicators by severity */}
            {(() => {
              const grouped = indicators.reduce((acc, indicator) => {
                const normSeverity = indicator.severity.toUpperCase();
                if (!acc[normSeverity]) {
                  acc[normSeverity] = [];
                }
                acc[normSeverity].push(indicator);
                return acc;
              }, {} as Record<string, FraudIndicator[]>);

              const severityOrder = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
              
              return (
                <Stack spacing={3}>
                  {severityOrder.map(severity => {
                    if (!grouped[severity] || grouped[severity].length === 0) return null;
                    
                    return (
                      <Box key={severity}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Chip
                            icon={getSeverityIcon(severity)}
                            label={`${severity} (${grouped[severity].length})`}
                            color={getSeverityColor(severity)}
                            size="medium"
                          />
                        </Box>
                        
                        <Stack spacing={1.5}>
                          {grouped[severity].map((indicator, index) => (
                            <Box 
                              key={index}
                              sx={{ 
                                p: 2,
                                borderLeft: 4,
                                borderColor: `${getSeverityColor(indicator.severity.toUpperCase())}.main`,
                                backgroundColor: severity === 'CRITICAL' ? 'rgba(211, 47, 47, 0.08)' : 
                                               severity === 'HIGH' ? 'rgba(237, 108, 2, 0.08)' :
                                               severity === 'MEDIUM' ? 'rgba(2, 136, 209, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                borderRadius: 1,
                                '&:hover': {
                                  boxShadow: 1
                                }
                              }}
                            >
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: `${getSeverityColor(indicator.severity.toUpperCase())}.dark`,
                                  mb: 0.5
                                }}
                              >
                                {indicator.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </Typography>
                              
                              <Typography variant="body2" sx={{ color: 'text.primary', mb: 1 }}>
                                {indicator.description}
                              </Typography>
                              
                              {/* Add explanatory text for certain indicator types */}
                              {indicator.type === 'extreme_price_outlier' && (
                                <Typography variant="caption" sx={{ color: 'info.main', fontStyle: 'italic', display: 'block', mb: 1 }}>
                                  💡 This award amount is significantly higher than typical contracts for similar products/services, 
                                  suggesting potential overpricing or irregular procurement.
                                </Typography>
                              )}
                              
                              {indicator.type === 'setaside_certification_mismatch' && (
                                <Typography variant="caption" sx={{ color: 'info.main', fontStyle: 'italic', display: 'block', mb: 1 }}>
                                  💡 This vendor received set-aside contracts intended for small businesses but lacks the required 
                                  certifications, which may violate federal procurement regulations.
                                </Typography>
                              )}
                              
                              {indicator.score_impact !== undefined && indicator.score_impact !== 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                  <Typography variant="caption" sx={{ 
                                    fontWeight: 500,
                                    color: indicator.score_impact > 0 ? 'error.main' : 'success.main'
                                  }}>
                                    Risk Score Impact: {indicator.score_impact > 0 ? '+' : ''}{indicator.score_impact}
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Handle both evidence and details fields for backward compatibility */}
                              {((indicator.evidence && Object.keys(indicator.evidence).length > 0) || (indicator.details && Object.keys(indicator.details).length > 0)) && (
                                <Box sx={{ 
                                  mt: 1.5, 
                                  p: 1.5, 
                                  backgroundColor: 'background.paper', 
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: 'divider'
                                }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Additional Details:
                                  </Typography>
                                  <Box sx={{ mt: 0.5 }}>
                                    {Object.entries(indicator.evidence || indicator.details || {}).map(([key, value]) => {
                                      // Format the key
                                      const formattedKey = key.replace(/_/g, ' ')
                                        .replace(/\b\w/g, l => l.toUpperCase());
                                      
                                      // Format the value based on type and content
                                      let formattedValue = '';
                                      
                                      if (key === 'max_amount' || key === 'mean_amount' || key === 'std_amount' || key === 'total_amount' || key.includes('amount')) {
                                        // Format currency values
                                        const num = typeof value === 'string' ? parseFloat(value) : typeof value === 'number' ? value : 0;
                                        formattedValue = `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
                                      } else if (key === 'max_z_score' || key === 'ratio_to_median') {
                                        // Format numbers with decimals
                                        const num = typeof value === 'string' ? parseFloat(value) : typeof value === 'number' ? value : 0;
                                        formattedValue = num.toFixed(1);
                                      } else if (key === 'violation_types' && Array.isArray(value)) {
                                        // Format violation types array
                                        formattedValue = value.join(', ');
                                      } else if (key === 'sample_violations' && Array.isArray(value)) {
                                        // Format sample violations specially
                                        return (
                                          <Box key={key} sx={{ mt: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                              Sample Violations:
                                            </Typography>
                                            {value.map((violation: any, idx: number) => (
                                              <Box key={idx} sx={{ ml: 2, mt: 0.5, p: 1, backgroundColor: 'grey.50', borderRadius: 0.5 }}>
                                                <Typography variant="caption" display="block" sx={{ fontWeight: 500 }}>
                                                  Award:{' '}
                                                  {violation.award_url ? (
                                                    <Link 
                                                      href={violation.award_url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      sx={{ 
                                                        color: 'primary.main',
                                                        textDecoration: 'none',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        '&:hover': { textDecoration: 'underline' }
                                                      }}
                                                    >
                                                      {violation.award_id} <OpenInNew sx={{ fontSize: 12 }} />
                                                    </Link>
                                                  ) : (
                                                    <span>{violation.award_id}</span>
                                                  )}
                                                  {violation.set_aside_type && (
                                                    <span> ({violation.set_aside_type})</span>
                                                  )}
                                                </Typography>
                                                <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                                  Amount: ${(violation.award_amount || 0).toLocaleString()}
                                                </Typography>
                                                {violation.description && (
                                                  <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                    {violation.description.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                                  </Typography>
                                                )}
                                              </Box>
                                            ))}
                                          </Box>
                                        );
                                      } else if (Array.isArray(value)) {
                                        // Format other arrays
                                        formattedValue = value.length > 0 ? value.join(', ') : 'None';
                                      } else if (typeof value === 'object' && value !== null) {
                                        // Skip complex objects for now or format them specially
                                        formattedValue = 'See details above';
                                      } else {
                                        // Default formatting
                                        formattedValue = String(value);
                                      }
                                      
                                      // Add explanatory text for certain fields
                                      let explanation = '';
                                      if (key === 'max_z_score') {
                                        explanation = ' (standard deviations from mean)';
                                      } else if (key === 'ratio_to_median') {
                                        explanation = 'x the median price';
                                      } else if (key === 'outlier_count') {
                                        explanation = ' awards flagged as outliers';
                                      }
                                      
                                      // Skip rendering if we already handled it specially (like sample_violations)
                                      if (key === 'sample_violations' && Array.isArray(value)) {
                                        return null;
                                      }
                                      
                                      return (
                                        <Typography key={key} variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.3 }}>
                                          • {formattedKey}: {formattedValue}{explanation}
                                        </Typography>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default FraudIndicatorChips;